import {DestroyRef, inject, Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, ReplaySubject} from 'rxjs';
import {tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


export interface LoggingConfig {
  level: number,
}

export interface AuthConfig {
  authority: string,
  clientId: string,
  scope: string,
  logLevel: number,
  renewTimeBeforeTokenExpiresInSeconds: number,
}

export interface PartnerConfig {
  apiBaseUrl: string,
}

export interface SchluesseldatenConfig {
  apiBaseUrl: string,
}

export interface ZahlungsConfig {
  apiBaseUrl: string,
}

export interface FakturierungsConfig {
  apiBaseUrl: string,
}

export interface BelegConfig {
  apiBaseUrl: string,
  naseBaseUrl?: string,
}

export interface ExportServiceConfig {
  apiBaseUrl: string,
}

export interface PortalConfig {
  targetOrigin: string,
}

export interface SentryConfig {
  enabled: boolean,
  debug: boolean,
  dsn: string,
  sampleRate: number,
}

export interface MatomoConfig {
  enabled: boolean,
  trackerUrl: string,
  siteId: string,
}

export interface IConfig {
  environment: string,
  logging: LoggingConfig,
  auth: AuthConfig,
  partner: PartnerConfig,
  schluesseldaten: SchluesseldatenConfig,
  zahlung: ZahlungsConfig,
  fakturierung: FakturierungsConfig,
  exportService: ExportServiceConfig,
  portal: PortalConfig,
  sentry: SentryConfig,
  matomo: MatomoConfig,
}

export class Config
  implements IConfig {

  constructor(
    public environment: string,
    public logging: LoggingConfig,
    public auth: AuthConfig,
    public partner: PartnerConfig,
    public schluesseldaten: SchluesseldatenConfig,
    public portal: PortalConfig,
    public sentry: SentryConfig,
    public matomo: MatomoConfig,
    public zahlung: ZahlungsConfig,
    public fakturierung: FakturierungsConfig,
    public exportService: ExportServiceConfig,
  ) {
  }
}

@Injectable()
export class ConfigService implements OnDestroy {

  private _destroyRef = inject(DestroyRef);
  private _httpClient = inject(HttpClient);

  private _loaded = new ReplaySubject<Config>();
  private _config_?: Config;

  ngOnDestroy() {
    this._loaded.complete();
  }

  get loaded$() {
    return this._loaded.pipe(
      takeUntilDestroyed(this._destroyRef),
    );
  }

  get config(): Config {
    return this._config_ as Config;
  }

  loadConfig() {
    return forkJoin([
      this.getConfig(),
    ]);
  }

  getConfig(): Observable<Config> {
    const url = '/assets/config/config.json';
    return this._httpClient.get<IConfig>(url)
      .pipe(
        tap(config => {
          this._config_ = new Config(
            config.environment,
            config.logging,
            config.auth,
            config.partner,
            config.schluesseldaten,
            config.portal,
            config.sentry,
            config.matomo,
            config.zahlung,
            config.fakturierung,
            config.exportService,
          );
          this._loaded.next(this._config_);
        }),
      );
  }
}
